import React, { useCallback, useMemo, useRef, useState } from 'react'

import { LocalArtifactData, LocalArtifactDataCollection, SelectLocalArtifactNativeProps } from './SelectLocalArtifactNative.types'

import './SelectLocalArtifactNative.scss'
import Button from '../atoms/Button/Button'
import { CheckCircle, FileEarmark, FolderPlus } from 'react-bootstrap-icons'
import { checkDotNet, checkPollinationPanel, PanelMessageOut } from '../hooks'
import { useEffect } from 'react'


// fileMatchPattern follows this convention:
// Uncaught Error: Provided filter string is not valid. Filter string should contain a 
// description of the filter, followed by a vertical bar and the filter pattern. Must 
// also separate multiple filter description and pattern pairs by a vertical bar. Must 
// separate multiple extensions in a filter pattern with a semicolon. Example: 
// "Image files (*.bmp, *.jpg)|*.bmp;*.jpg|All files (*.*)|*.*"
const SelectLocalArtifactNative: React.FC<SelectLocalArtifactNativeProps> = ({ 
  key=performance.now().toString(),
  value,
  buttonLabel=undefined,
  disabled=false,
  fileMatchPattern='All Files (*.*)|*.*',
  mode='file',
  onChange,
}) => {
  /** 
   * Get info from system
   */
  const handleSelectNative = useCallback(() => {
    if(!checkDotNet()) throw new Error('Use handleSelectNative from a Pollination CAD Plugin.')
    
    let res: PanelMessageOut

    try {
      if (mode === 'file') {
        res = window.parent.chrome.webview.hostObjects.sync.study
          .GetArtifact(key, fileMatchPattern)
      } else {
        res = window.parent.chrome.webview.hostObjects.sync.study
          .GetFolder(key)
      }
    } catch (error) {
      // common error is incorrect fileMatchPattern
      console.error(error)
    }

    let root = ''
    if (mode === 'recursive') {
      if (res && typeof res.data === 'string') {
        root = res.data
        res = window.parent.chrome.webview.hostObjects.sync.study
            .GetFilePaths(root)
      } else {
        res = undefined
      }
    }

    if (res && (mode === 'file' || mode === 'directory') 
      && typeof res.data === 'string') {
      setResLabel(res.data)
      onChange({ pollination_local_path: res.data 
        } as LocalArtifactData)
    } else if (res && (mode === 'recursive') 
      && typeof res.data === 'string') {
      setResLabel(root)
      onChange({ local_root: root, 
          inner_files: JSON.parse(res.data) 
        } as LocalArtifactDataCollection)
    } else {
      setResLabel('Error')
      console.error('Message is empty...')
    }
  }, [fileMatchPattern, key, mode])


  /**
   * Set label for controlled value
   * @param mode mode
   * @param value controlled value
   */
  const setLabel = (mode: string, value: any) => {
    if (value && (mode === 'file' || mode === 'directory')) {
      setResLabel(value.pollination_local_path)
    } else if (value && (mode === 'recursive')) {
      setResLabel(value.local_root)
    } else {
      setResLabel('Error')
    }
  }
  const [defaultLabel, setDefaultLabel] = useState<string | undefined>()
  const [resLabel, setResLabel] = useState<string | undefined>()

  /**
   * Update label
   */
  useEffect(() => {
    if (buttonLabel) {
      setDefaultLabel(buttonLabel)
      return
    }

    switch (mode) {
      case 'file':
        setDefaultLabel('Select File')
        break
      case 'directory':
      case 'recursive':
        setDefaultLabel('Select Folder')
        break
      default:
        break
    }
  }, [mode, buttonLabel])

  useEffect(() => {
    // Initial values
    value && onChange(value)
    value && setLabel(mode, value)
  }, [])

  return (
    <Button
      onClick={handleSelectNative}
      disabled={disabled || !checkPollinationPanel()}
    >
      <div
        style={{
          display:'flex',
        }}
      >
        {value ? <CheckCircle size={16}/> : mode === 'file' ? <FileEarmark size={16}/> : <FolderPlus  size={16}/> }
      </div>
      {resLabel ?? defaultLabel}
    </Button>
  )
}

export default SelectLocalArtifactNative